import { useCallback, useState } from "react";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { FiltersTypes } from "@common/types/filters/general";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import {
    ARCHIVE_FILTER_ITEMS,
    ArchiveFilter,
} from "../templates/archiveFilter";
import { useChartOfAccountsTemplatesView } from "./useChartOfAccountsTemplatesView";
import { ChartOfAccountsTemplatesList } from "./ChartOfAccountsTemplatesList";
import { ChartOfAccountsTemplateModal } from "./ChartOfAccountsTemplateModal";
import { ChartOfAccountsTemplateEditorDrawer } from "./editor/ChartOfAccountsTemplateEditorDrawer";
import { COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP } from "./constants";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";

export const ChartOfAccountsTemplatesPage = () => {
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<FiltersTypes>({});
    const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("active");
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Managers and above can manage templates; Members get a read-only view.
    // Delete is gated separately by the backend's per-row `canDelete` value.
    const canManage = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_CHART_OF_ACCOUNTS_TEMPLATES,
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>((patch) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const [editorTemplate, setEditorTemplate] =
        useState<ChartOfAccountsTemplate | null>(null);
    const handleCloseEditor = useCallback(() => {
        setEditorTemplate(null);
    }, []);

    const { view, defaultView, viewConfig, handleViewChange } =
        useChartOfAccountsTemplatesView();

    return (
        <main>
            <TablePageHeader
                search={search}
                onSearchChange={setSearch}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={[
                    FilterTabs.ENTITY_INDUSTRY,
                    FilterTabs.ENTITY_BASIS,
                    FilterTabs.LEGAL_TYPE,
                ]}
                defaultTab={FilterTabs.ENTITY_INDUSTRY}
                defaultFilters={{}}
                isFullPage
            >
                <HStack gap={8} className="ml-auto">
                    <Picker.Select
                        items={ARCHIVE_FILTER_ITEMS}
                        value={archiveFilter}
                        onValueChange={(value) =>
                            setArchiveFilter(value ?? "active")
                        }
                    />
                    <ColumnSettings
                        view={view}
                        viewConfig={viewConfig}
                        defaultView={defaultView}
                        onViewChange={handleViewChange}
                    />
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyDisplayPosition={canManage ? "tooltip" : "none"}
                        hotKeyOptions={{
                            description: "New template",
                            preventDefault: true,
                        }}
                        tooltip={
                            canManage
                                ? undefined
                                : COA_TEMPLATE_MANAGE_FORBIDDEN_TOOLTIP
                        }
                    >
                        <Button
                            variant="primary"
                            size="md"
                            disabled={!canManage}
                            onClick={() => setIsAddModalOpen(true)}
                        >
                            New template
                        </Button>
                    </WithHotkey>
                </HStack>
            </TablePageHeader>

            <ChartOfAccountsTemplatesList
                search={search}
                selectedIndustries={filters.entityIndustries}
                selectedBases={filters.entityBases}
                selectedLegalTypes={filters.legalTypes}
                archiveFilter={archiveFilter}
                view={view}
                canManage={canManage}
                onViewChange={handleViewChange}
                onOpenEditor={setEditorTemplate}
                onCreateTemplate={() => setIsAddModalOpen(true)}
            />

            <ChartOfAccountsTemplateModal
                show={isAddModalOpen}
                onHide={() => setIsAddModalOpen(false)}
                onCreated={setEditorTemplate}
            />

            <ChartOfAccountsTemplateEditorDrawer
                template={editorTemplate}
                isOpen={!!editorTemplate}
                onClose={handleCloseEditor}
            />
        </main>
    );
};
